'use client'

import { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import { Swiper as SwiperType } from 'swiper'
import 'swiper/css'
import 'swiper/css/a11y'
import { A11y } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { ProductCard } from '@/widgets/ProductCard'
import { SectionElements } from '@/widgets/SectionElements'

import { TProduct } from '@/shared/types/product-types'
import { ItemLoader } from '@/shared/ui/Skeletons/variants/ItemLoader/ItemLoader'
import { SliderNextButton, SliderPrevButton } from '@/shared/ui/SliderButtons'

import cl from './recommended-products.module.scss'

type RecommendedProductsProps = {
	title: string
	products: TProduct[]
	isLoading?: boolean
	skeletonCount?: number
	variant?: 'section' | 'subsection'
	action?: {
		text: ReactNode
		onClick: () => void
	}
}

export const RecommendedProducts: FC<RecommendedProductsProps> = ({
	title,
	products,
	isLoading,
	skeletonCount = 5,
	variant = 'section',
	action
}) => {
	const isSubsection = variant === 'subsection'
	const [isBeginning, setIsBeginning] = useState(true)
	const [isEnd, setIsEnd] = useState(false)
	const [canSlide, setCanSlide] = useState(false)
	const [swiper, setSwiper] = useState<SwiperType | null>(null)
	const sectionVariant = isSubsection ? 'subsection' : 'slider'

	const updateSliderState = useCallback((swiperInstance: SwiperType) => {
		setSwiper(swiperInstance)
		setIsBeginning(swiperInstance.isBeginning)
		setIsEnd(swiperInstance.isEnd)
		setCanSlide(!swiperInstance.isLocked)
	}, [])

	const handleSlidePrev = useCallback(() => {
		swiper?.slidePrev()
	}, [swiper])

	const handleSlideNext = useCallback(() => {
		swiper?.slideNext()
	}, [swiper])

	useEffect(() => {
		const handleSwipeOnKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'ArrowLeft' && !isBeginning) handleSlidePrev()
			if (event.key === 'ArrowRight' && !isEnd) handleSlideNext()
		}

		document.body.addEventListener('keydown', handleSwipeOnKeyDown)

		return () => {
			document.body.removeEventListener('keydown', handleSwipeOnKeyDown)
		}
	}, [handleSlideNext, handleSlidePrev, isBeginning, isEnd])

	if (isLoading) {
		return (
			<SectionElements title={title} variant={sectionVariant} action={action}>
				<div className={cl.skeletons}>
					{Array.from({ length: skeletonCount }, (_, index) => (
						<div className={cl.slide} key={index}>
							<ItemLoader width={288} />
						</div>
					))}
				</div>
			</SectionElements>
		)
	}

	if (!products.length) return null

	const showNavigation = canSlide

	return (
		<SectionElements title={title} variant={sectionVariant} action={action}>
			<div className={cl.slider}>
				<Swiper
					className={cl.swiper}
					modules={[A11y]}
					slidesPerView="auto"
					spaceBetween={0}
					breakpoints={{
						1440: {
							slidesPerView: 5
						}
					}}
					watchOverflow={true}
					observer={true}
					observeParents={true}
					onSwiper={updateSliderState}
					onSlideChange={updateSliderState}
					onBreakpoint={updateSliderState}
					onResize={updateSliderState}>
					{products.map((product, index) => (
						<SwiperSlide key={product.ID} className={cl.slide}>
							<ProductCard data={product} position={index} />
						</SwiperSlide>
					))}
				</Swiper>
				{showNavigation && (
					<div className={cl.buttons}>
						<SliderPrevButton className={cl.buttons__prevButton} disabled={isBeginning} onClick={handleSlidePrev} />
						<SliderNextButton className={cl.buttons__nextButton} disabled={isEnd} onClick={handleSlideNext} />
					</div>
				)}
			</div>
		</SectionElements>
	)
}
